import React, { useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import searchIcon from "../../../../images/searchIcon.png";
import "react-toastify/dist/ReactToastify.css";
import { logEvent, ITEM_CREATION_EVENTS } from "../../../../utils/amplitude";
import { Container } from "../../../../globalstyled";
import { AppCSS, Spacer, TxtInput } from "../../../../components";
import { ColDiv } from "../../../../components/misc/misc.styled";
import LeadsTableHeaderCard from "../../../../components/table/CustomTableHeaderCard";
import LeadsTableBodyCard from "../../../../components/table/CustomTableBodyCard";
import { Supplier } from "../../../../types/ItemsCreation";
import { useArchiveSupplier, useCreateSupplier, useUpdateSupplier } from "../../../../apolloClient/hooks/itemCreation/createHooks";
import CancelAndSubmitButtons from "../ItemCreationComponent/SubmitAndCancelButton";
import SideDrawer from "../SideDrawer";
import SideDrawerHeader from "../SideDrawer/SideDrawerHeader";
import { FormTitleTypography } from "../itemCreation.styled";
import SearchAndCreateComponent from "../ItemCreationComponent/SearchAndCreateComponent";
import Pagination from "../ItemCreationComponent/Pagination";
import EmptyTable from "../../../../components/table/EmptyTable";
import TableViewDetails from "../../../../components/table/TableViewDetails";
import ConfirmationDialog from "../../../../components/dialogue/ConfirmationDialog";

interface SuppliersTableProps {
  suppliersData: Supplier[];
  refetchSuppliers?: () => void;
  permissions?: boolean;
}

const SUPPLIERS_TABLE_HEADER_LIST = [
  { text: "ID", width: "10%" },
  { text: "Name", width: "25%" },
  { text: "Email", width: "25%" },
  { text: "Website", width: "20%" },
  { text: "Actions", width: "20%" },
];

const SuppliersTable: React.FC<SuppliersTableProps> = ({ suppliersData, refetchSuppliers, permissions }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSupplierForDelete, setSelectedSupplierForDelete] = useState<Supplier | null>(null);

  const { archiveSupplier } = useArchiveSupplier();
  // Drawer and form state for create/edit
  const [openDrawer, setOpenDrawer] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ name: "", email: "", website: "" });
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const { createSupplier } = useCreateSupplier();
  const { updateSupplier } = useUpdateSupplier();

  const handleDrawerOpen = () => {
    setSupplierForm({ name: "", email: "", website: "" });
    setSelectedSupplier(null);
    setOpenDrawer(true);
  };
  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSupplierForm({ name: "", email: "", website: "" });
    setSelectedSupplier(null);
  };
  const handleEditClick = (supplier: Supplier) => {
    setSupplierForm({
      name: supplier.name || "",
      email: supplier.email || "",
      website: supplier.website || "",
    });
    setSelectedSupplier(supplier);
    setOpenDrawer(true);
  };
  const handleFormChange = (field: string, value: string) => {
    setSupplierForm((prev) => ({ ...prev, [field]: value }));
  };
  const onTapSubmitForm = async () => {
    if (!supplierForm.name) {
      toast("Please enter a supplier name!", { type: "error" });
      return;
    }
    if (selectedSupplier) {
      // Edit mode
      const res = await updateSupplier({ id: selectedSupplier.id, ...supplierForm });
      if (res) {
        toast("Supplier updated successfully.", { type: "success" });
        logEvent(ITEM_CREATION_EVENTS.SUPPLIER_UPDATED, { supplier_id: selectedSupplier.id, supplier_name: supplierForm.name, timestamp: new Date().toISOString() });
        refetchSuppliers && refetchSuppliers();
        handleDrawerClose();
      } else {
        toast("Failed to update supplier.", { type: "error" });
      }
    } else {
      // Create mode
      const res = await createSupplier(supplierForm);
      if (res) {
        toast("Supplier created successfully.", { type: "success" });
        logEvent(ITEM_CREATION_EVENTS.SUPPLIER_CREATED, { supplier_name: supplierForm.name, timestamp: new Date().toISOString() });
        refetchSuppliers && refetchSuppliers();
        handleDrawerClose();
      } else {
        toast("Failed to create supplier.", { type: "error" });
      }
    }
  };

  const handleArchive = (row: Supplier) => {
    setSelectedSupplierForDelete(row);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (selectedSupplierForDelete) {
      await archiveSupplier(selectedSupplierForDelete.id);
      toast(`${selectedSupplierForDelete.name} archived!`, { type: "success" });
      logEvent(ITEM_CREATION_EVENTS.SUPPLIER_ARCHIVED, { supplier_id: selectedSupplierForDelete.id, supplier_name: selectedSupplierForDelete.name, timestamp: new Date().toISOString() });
      refetchSuppliers && refetchSuppliers();
      setOpenDeleteDialog(false);
      setSelectedSupplierForDelete(null);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedSupplierForDelete(null);
  };

  const handleSearchIconClick = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const filterSuppliers = () => {
    if (keyword.length > 0 && Array.isArray(suppliersData)) {
      return suppliersData.filter((item) => {
        const nameStr = `${item.name}`;
        return nameStr.toLowerCase().includes(keyword.toLowerCase());
      });
    }
    return suppliersData;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    setPage((prevPage) =>
      Math.min(
        prevPage + 1,
        Math.ceil(filterSuppliers().length / rowsPerPage) - 1
      )
    );
  };

  const SuppliersTableComponent = () => {
    const paginatedData = filterSuppliers().slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    const totalPages = Math.ceil(filterSuppliers().length / rowsPerPage);

    return (
      <Box sx={{ width: "100%" }}>
        {/* Search and Create Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              height: "44px",
              width: { xs: "100%", md: "320px" },
              transition: "border-color 0.2s, box-shadow 0.2s",
              "&:focus-within": {
                borderColor: "#4b87ed",
                boxShadow: "0 0 0 3px rgba(75, 135, 237, 0.1)",
              },
            }}
          >
            <Box
              component="img"
              src={searchIcon}
              sx={{ width: 16, height: 16, cursor: "pointer", margin: "0 12px" }}
              onClick={handleSearchIconClick}
            />
            <TxtInput
              placeholder="Search suppliers"
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </Box>

          {permissions && (
            <Box
              component="button"
              onClick={handleDrawerOpen}
              sx={{
                padding: "10px 20px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s",
                backgroundColor: "#4b87ed",
                color: "white",
                border: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#3a6fd8",
                },
              }}
            >
              + Create new supplier
            </Box>
          )}
        </Box>

        {/* Table */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Desktop Table Header */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              padding: "14px 20px",
              backgroundColor: "#f3f4f6",
              borderBottom: "1px solid #e5e7eb",
              fontWeight: 600,
              fontSize: 12,
              color: "#6b7280",
              textTransform: "uppercase",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "10%", minWidth: "50px" }}>ID</Box>
            <Box sx={{ width: "25%", minWidth: "100px" }}>Name</Box>
            <Box sx={{ width: "25%", minWidth: "100px" }}>Email</Box>
            <Box sx={{ width: "20%", minWidth: "80px" }}>Website</Box>
            <Box sx={{ width: "20%", minWidth: "100px", textAlign: "right" }}>Actions</Box>
          </Box>

          {/* Table Body */}
          {filterSuppliers().length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "48px 24px",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 15, color: "#6b7280", margin: 0 }}>
                No suppliers available
              </Typography>
            </Box>
          ) : (
            paginatedData.map((row, rowIndex) => (
              <Box
                key={`supplier-${rowIndex}`}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "center" },
                  padding: { xs: "14px 16px", md: "14px 20px" },
                  borderBottom: "1px solid #f0f0f0",
                  gap: { xs: 1, md: 0 },
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                {/* ID */}
                <Box sx={{ width: { xs: "100%", md: "10%" }, minWidth: { md: "50px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    ID
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#1a1a2e" }}>
                    {row.id}
                  </Typography>
                </Box>

                {/* Name */}
                <Box sx={{ width: { xs: "100%", md: "25%" }, minWidth: { md: "100px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    Name
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#374151" }}>
                    {row.name}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ width: { xs: "100%", md: "25%" }, minWidth: { md: "100px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    Email
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#374151" }}>
                    {row.email || "-"}
                  </Typography>
                </Box>

                {/* Website */}
                <Box sx={{ width: { xs: "100%", md: "20%" }, minWidth: { md: "80px" } }}>
                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: "2px",
                    }}
                  >
                    Website
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#374151" }}>
                    {row.website || "-"}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "20%" },
                    minWidth: { md: "100px" },
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 1,
                    flexWrap: "wrap",
                    marginTop: { xs: 1, md: 0 },
                  }}
                >
                  {permissions && (
                    <>
                      <Box
                        component="button"
                        onClick={() => handleEditClick(row)}
                        sx={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          backgroundColor: "#e5e7eb",
                          color: "#374151",
                          border: "none",
                          "&:hover": {
                            backgroundColor: "#d1d5db",
                          },
                        }}
                      >
                        Edit
                      </Box>
                      <Box
                        component="button"
                        onClick={() => handleArchive(row)}
                        sx={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                          border: "none",
                          "&:hover": {
                            backgroundColor: "#fecaca",
                          },
                        }}
                      >
                        Archive
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ))
          )}

          {/* Pagination */}
          {filterSuppliers().length > 0 && totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-end" },
                padding: "14px 20px",
                gap: 1,
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
              }}
            >
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <Typography sx={{ fontSize: 14, color: "#6b7280", margin: "0 8px" }}>
                Page {page + 1} of {totalPages}
              </Typography>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {SuppliersTableComponent()}
      <SideDrawer open={openDrawer} onClose={handleDrawerClose}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: "100%",
          }}
        >
          <SideDrawerHeader
            handleDrawerClose={handleDrawerClose}
            labelTitle={selectedSupplier ? "Edit Supplier" : "Create new Supplier"}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: { xs: "100%", md: "97%" },
            }}
          >
            {/* Supplier Name */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#1a1a2e",
                }}
              >
                Supplier Name *
              </Typography>
              <TxtInput
                placeholder="Enter supplier name"
                onChange={(val: string) => handleFormChange("name", val)}
                required={true}
                value={supplierForm.name}
                background="inherit"
                borderColor="rgba(173, 173, 173, 1)"
                borderRadius={10}
                height={50}
              />
            </Box>

            {/* Email */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#1a1a2e",
                }}
              >
                Email
              </Typography>
              <TxtInput
                placeholder="Enter email"
                onChange={(val: string) => handleFormChange("email", val)}
                value={supplierForm.email}
                background="inherit"
                borderColor="rgba(173, 173, 173, 1)"
                borderRadius={10}
                height={50}
              />
            </Box>

            {/* Website */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#1a1a2e",
                }}
              >
                Website
              </Typography>
              <TxtInput
                placeholder="Enter website"
                onChange={(val: string) => handleFormChange("website", val)}
                value={supplierForm.website}
                background="inherit"
                borderColor="rgba(173, 173, 173, 1)"
                borderRadius={10}
                height={50}
              />
            </Box>
          </Box>
        </Box>

        <CancelAndSubmitButtons
          onCancel={handleDrawerClose}
          onCreate={onTapSubmitForm}
          submitText={selectedSupplier ? "Update" : "Create"}
        />
      </SideDrawer>
      <ConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Archive"
        message={`Are you sure you want to archive "${selectedSupplierForDelete?.name}"?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default SuppliersTable;
